import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import ProfileModal from './ProfileModal';
import logoWhite from '../assets/logo-white.svg';
import logoIcon from '../assets/logo-icon.svg';

const NAV_ITEMS = [
  { id: 'dashboard',    icon: '📊', label: 'Dashboard',        path: '/dashboard' },
  { id: 'agenda',       icon: '📅', label: 'Agenda',           path: '/agenda' },
  { id: 'separator0',  icon: null, label: null,                path: null },
  { id: 'clients',      icon: '👥', label: 'Clients',          path: '/clients' },
  { id: 'contacts',     icon: '🤝', label: 'Contacts',         path: '/contacts' },
  { id: 'contrats',     icon: '📋', label: 'Contrats',         path: '/contrats' },
  { id: 'opportunites', icon: '💼', label: 'Opportunités',     path: '/opportunites' },
  { id: 'devis',        icon: '📄', label: 'Devis',            path: '/devis' },
  { id: 'factures',     icon: '🧾', label: 'Factures',         path: '/factures' },
  { id: 'audits',       icon: '🔬', label: 'Audits data',      path: '/audits' },
  { id: 'separator',   icon: null, label: null,                path: null },
  { id: 'missions',     icon: '📁', label: 'Missions & RGPD',  path: '/missions' },
  { id: 'conformite',   icon: '🔒', label: 'Conformité',       path: '/conformite' },
  { id: 'separator2',  icon: null, label: null,                path: null },
  { id: 'import',       icon: '⬆',  label: 'Import données',  path: '/import' },
  { id: 'comptabilite', icon: '💰', label: 'Comptabilité',     path: '/comptabilite' },
];

export default function Sidebar({ badges, isMobile, mobileOpen, onClose }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const items = isAdmin ? [...NAV_ITEMS, { id: 'admin', icon: '⚙️', label: 'Administration', path: '/admin' }] : NAV_ITEMS;

  const effectiveCollapsed = isMobile ? false : collapsed;
  const sidebarWidth = isMobile ? 260 : (collapsed ? 60 : 220);

  return (
    <>
    <div style={{
      width: sidebarWidth,
      minWidth: sidebarWidth,
      flexShrink: 0,
      position: isMobile ? 'fixed' : 'sticky',
      top: 0,
      left: isMobile ? (mobileOpen ? 0 : -sidebarWidth) : 'auto',
      zIndex: isMobile ? 200 : 'auto',
      background: '#12141f',
      borderRight: '1px solid #2a2d3e',
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      transition: isMobile ? 'left 0.25s ease' : 'width 0.2s, min-width 0.2s',
      overflow: isMobile ? 'hidden' : 'visible',
    }}>
      {/* Header */}
      <div style={{ padding: effectiveCollapsed ? '16px 0' : '16px 16px', borderBottom: '1px solid #2a2d3e', display: 'flex', alignItems: 'center', gap: 10, minHeight: 60 }}>
        {effectiveCollapsed ? (
          <img src={logoIcon} alt="LA DONNÉE" style={{ width: 32, height: 32, objectFit: 'contain', margin: '0 auto' }} />
        ) : (
          <>
            <img src={logoWhite} alt="LA DONNÉE" style={{ height: 28, objectFit: 'contain', flex: 1, minWidth: 0 }} />
            {isMobile && (
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20, padding: '0 2px', flexShrink: 0 }}>✕</button>
            )}
          </>
        )}
      </div>

      {/* Recherche globale */}
      {!effectiveCollapsed && (
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #2a2d3e' }}>
          <GlobalSearch collapsed={false} />
        </div>
      )}
      {effectiveCollapsed && (
        <div style={{ padding: '8px 0', borderBottom: '1px solid #2a2d3e', display: 'flex', justifyContent: 'center' }}>
          <GlobalSearch collapsed={true} />
        </div>
      )}

      {/* Nav */}
      <nav className="sidebar-nav" style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
        {items.map(item => {
          if (item.id.startsWith('separator')) {
            return <div key={item.id} style={{ height: 1, background: '#2a2d3e', margin: '8px 0' }} />;
          }
          const isActive = location.pathname === item.path;
          const badge = badges?.[item.id];
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: effectiveCollapsed ? '10px 0' : '10px 16px',
                justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                background: isActive ? '#41B07320' : 'transparent',
                border: 'none',
                borderLeft: isActive ? '3px solid #41B073' : '3px solid transparent',
                color: isActive ? '#41B073' : '#94a3b8',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: isActive ? 600 : 400,
                transition: 'all 0.15s',
                position: 'relative',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
              {!effectiveCollapsed && <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>}
              {!effectiveCollapsed && badge > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{badge}</span>
              )}
              {effectiveCollapsed && badge > 0 && (
                <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, background: '#ef4444', borderRadius: '50%' }} />
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #2a2d3e', padding: effectiveCollapsed ? '12px 0' : '12px 16px' }}>
        {/* Collapse toggle (desktop only) */}
        {!isMobile && (
          <button
            onClick={() => setCollapsed(c => !c)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 8, padding: '8px 0',
              background: 'transparent', border: 'none', color: '#64748b',
              cursor: 'pointer', fontSize: 13, marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 16 }}>{collapsed ? '▶' : '◀'}</span>
            {!collapsed && <span>Réduire</span>}
          </button>
        )}

        {/* User info + cloche notifications */}
        {!effectiveCollapsed && (
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              onClick={() => setShowProfile(true)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{user?.prenom} {user?.nom}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{user?.role === 'admin' ? 'Administrateur' : 'Commercial'}</div>
            </button>
            <NotificationBell />
          </div>
        )}
        {effectiveCollapsed && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <NotificationBell />
          </div>
        )}
        <button
          onClick={logout}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
            gap: 8, padding: '6px 0',
            background: 'transparent', border: 'none', color: '#ef4444',
            cursor: 'pointer', fontSize: 13,
          }}
        >
          <span>🚪</span>
          {!effectiveCollapsed && <span>Déconnexion</span>}
        </button>
      </div>
    </div>
    {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
    </>
  );
}
